function extractPrice(text) {
  if (!text) return '';
  const matches = text.match(/[$€£]\s?\d[\d,]*(?:\.\d{2})?/g);
  if (!matches || matches.length === 0) return '';
  return matches[matches.length - 1].replace(/\s+/g, ' ').trim();
}

const productDataCache = new Map();
const productServingCountCache = new Map();

function parsePriceParts(priceText) {
  if (!priceText) return null;
  const match = priceText.match(/([$€£])\s?(\d[\d,]*(?:\.\d{2})?)/);
  if (!match) return null;

  const symbol = match[1];
  const amount = Number.parseFloat(match[2].replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;

  return { symbol, amount };
}

function formatAmountWithSymbol(amount, symbol = '$') {
  if (!Number.isFinite(amount)) return '';
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
}

function getQuantity(root) {
  const quantityInput =
    root.querySelector("input[name='quantity']") || document.querySelector("input[name='quantity']");
  const quantity = Number.parseInt(quantityInput?.value || '1', 10);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

function getSelectedVariantId(root) {
  const variantInput =
    root.querySelector("input[name='id']") ||
    document.querySelector("product-form-component input[name='id']") ||
    document.querySelector("input[name='id']");

  const variantId = Number.parseInt(variantInput?.value || '', 10);
  return Number.isFinite(variantId) ? variantId : null;
}

function getProductUrl(root) {
  const picker =
    root.querySelector('variant-picker') ||
    document.querySelector("variant-picker[data-template-product-match='true']") ||
    document.querySelector('variant-picker');
  const productFormComponent =
    root.closest('product-form-component') ||
    root.querySelector('product-form-component') ||
    document.querySelector('product-form-component[data-product-url]');

  const rawUrl = picker?.dataset?.productUrl || productFormComponent?.dataset?.productUrl;
  if (!rawUrl) return '';

  try {
    const normalized = new URL(rawUrl, window.location.origin);
    return normalized.pathname.replace(/\/$/, '');
  } catch {
    return rawUrl.split('?')[0].replace(/\/$/, '');
  }
}

async function getProductData(root) {
  const productUrl = getProductUrl(root);
  if (!productUrl) return null;

  if (productDataCache.has(productUrl)) {
    return productDataCache.get(productUrl);
  }

  const request = fetch(`${productUrl}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);

  productDataCache.set(productUrl, request);
  return request;
}

function getServingCount(text) {
  if (!text) return null;
  const match = text.match(/(\d+(?:\.\d+)?)\s*servings?/i);
  if (!match) return null;

  const servings = Number.parseFloat(match[1]);
  return Number.isFinite(servings) && servings > 0 ? servings : null;
}

function getTotalSachets(text) {
  if (!text) return null;

  let total = 0;
  const matches = text.matchAll(/(\d+(?:\.\d+)?)\s*sachets?\b/gi);
  for (const match of matches) {
    const amount = Number.parseFloat(match[1]);
    if (Number.isFinite(amount) && amount > 0) {
      total += amount;
    }
  }

  return total > 0 ? total : null;
}

function getServingCountFromText(text) {
  if (!text) return null;

  const normalizedText = text.replace(/\u00a0/g, ' ');

  const includesMatch = normalizedText.match(/includes\s*:?\s*([\s\S]*)/i);
  const includesText = includesMatch?.[1] || '';

  const includesServings = getServingCount(includesText);
  if (includesServings) return includesServings;

  const includesSachets = getTotalSachets(includesText);
  if (includesSachets) return includesSachets;

  const allServings = getServingCount(normalizedText);
  if (allServings) return allServings;

  return getTotalSachets(normalizedText);
}

function getServingCountFromDescription(descriptionHtml) {
  if (!descriptionHtml) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(descriptionHtml, 'text/html');
  const bodyText = doc.body?.textContent || '';
  return getServingCountFromText(bodyText);
}

function getServingCountFromProductDetails(root) {
  const productDetails =
    root.closest('.product-details') ||
    document.querySelector('.product-details') ||
    root.closest('.product-information') ||
    document.querySelector('.product-information');
  if (!productDetails) return null;

  const descriptionNodes = productDetails.querySelectorAll(
    'rte-formatter, .text-block.rte, .text-block.paragraph'
  );

  let text = '';
  if (descriptionNodes.length > 0) {
    text = Array.from(descriptionNodes)
      .map((node) => node.textContent || '')
      .join('\n');
  } else {
    text = productDetails.textContent || '';
  }

  return getServingCountFromText(text);
}

async function getFallbackServingCount(root) {
  const domServingCount = getServingCountFromProductDetails(root);
  if (domServingCount) return domServingCount;

  const productUrl = getProductUrl(root);
  if (!productUrl) return null;

  if (productServingCountCache.has(productUrl)) {
    return productServingCountCache.get(productUrl);
  }

  const request = getProductData(root)
    .then((productData) => getServingCountFromDescription(productData?.description || ''))
    .catch(() => null);

  productServingCountCache.set(productUrl, request);
  return request;
}

function multiplyPriceByQuantity(priceText, quantity) {
  if (!priceText) return '';

  const match = priceText.match(/([$€£])(\s?)(\d[\d,]*(?:\.\d{2})?)/);
  if (!match) return priceText;

  const symbol = match[1];
  const spacing = match[2] || '';
  const rawAmount = match[3];
  const amount = Number.parseFloat(rawAmount.replace(/,/g, ''));

  if (!Number.isFinite(amount)) return priceText;

  const total = amount * quantity;
  const formattedAmount = total.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${symbol}${spacing}${formattedAmount}`;
}

function stripTrailingPrice(text) {
  if (!text) return '';
  return text.replace(/\s*-\s*[$€£]\s?\d[\d,]*(?:\.\d{2})?\s*$/, '').trim();
}

function getPriceFromElement(priceEl) {
  if (!priceEl) return null;

  const price = extractPrice(priceEl.textContent || '');
  if (!price) return null;

  return {
    price,
    compareAtPrice: extractPrice(priceEl.querySelector('s')?.textContent || ''),
  };
}

function getSelectedPurchasePrice(root) {
  const subscriptionSelected =
    root.querySelector("[data-stayai-purchase-option='subscription'].is-selected") ||
    root.querySelector("label[for='purchaseTypeSubscription'].is-selected");

  if (subscriptionSelected) {
    const subscriptionPrice = getPriceFromElement(
      subscriptionSelected.querySelector('[data-stayai-subscription-price]') ||
        root.querySelector('[data-stayai-subscription-price]')
    );
    if (subscriptionPrice) return subscriptionPrice;
  }

  const oneTimeSelected =
    root.querySelector("[data-stayai-purchase-option='onetime'].is-selected") ||
    root.querySelector("label[for='purchaseTypeOneTime'].is-selected");

  if (oneTimeSelected) {
    const oneTimePrice = getPriceFromElement(
      oneTimeSelected.querySelector('[data-stayai-onetime-price]') ||
        root.querySelector('[data-stayai-onetime-price]')
    );
    if (oneTimePrice) return oneTimePrice;
  }

  const selectedLabel =
    root.querySelector('.stayai-subscription label.is-selected') ||
    root.querySelector(".stayai-subscription input[type='radio']:checked")?.closest('label');

  if (!selectedLabel) return null;

  return getPriceFromElement(
    selectedLabel.querySelector('[data-stayai-subscription-price], [data-stayai-onetime-price]')
  );
}

function stripButtonTrailingPrice(text) {
  return stripTrailingPrice(text).replace(/\s*[$â‚¬Â£]\s?\d[\d,]*(?:\.\d{2})?\s*$/, '').trim();
}

function renderButtonPrice(el, label, compareAtPrice, price) {
  const priceText = compareAtPrice && compareAtPrice !== price ? `${compareAtPrice} - ${price}` : price;
  const nextText = [label, priceText].filter(Boolean).join(' ').trim();

  if (el.dataset.lastRenderedText === nextText) return;

  el.textContent = '';

  if (label) {
    const labelNode = document.createElement('span');
    labelNode.textContent = label;
    el.appendChild(labelNode);
  }

  if (priceText) {
    if (label) {
      el.appendChild(document.createTextNode(' '));
    }

    const priceNode = document.createElement('span');
    priceNode.className = 'is-desktop-only';

    if (compareAtPrice && compareAtPrice !== price) {
      const compareAtNode = document.createElement('s');
      compareAtNode.className = 'add-to-cart-text__compare-at';
      compareAtNode.textContent = compareAtPrice;
      priceNode.appendChild(compareAtNode);
      priceNode.appendChild(document.createTextNode(' - '));
    }

    priceNode.appendChild(document.createTextNode(price));
    el.appendChild(priceNode);
  }

  el.dataset.lastRenderedText = nextText;
}

function getSelectedVariantPerServing(root) {
  // NIM-4044: skip an option rendered hidden (one offered value); its checked input is not the pack.
  const checkedSelector = ".variant-option:not([hidden]) .variant-option__button-label input[type='radio']:checked";
  const checkedInput = root.querySelector(checkedSelector) || document.querySelector(checkedSelector);
  const selectedLabel = checkedInput?.closest('.variant-option__button-label');
  if (!selectedLabel) return null;

  const text = selectedLabel.textContent || '';
  const match = text.match(/([$€£]\s?\d[\d,]*(?:\.\d{2})?)\s*per\s*serving/i);
  if (!match) return null;

  const price = parsePriceParts(match[1]);
  if (!price) return null;

  return price;
}

function upsertServingTextAfterPrice(priceEl, text, className) {
  if (!priceEl || !priceEl.parentElement || !text) return;

  let servingEl = priceEl.parentElement.querySelector(`.${className}`);
  if (!servingEl) {
    servingEl = document.createElement('span');
    servingEl.className = className;
    priceEl.insertAdjacentElement('afterend', servingEl);
  }

  if (servingEl.textContent !== text) {
    servingEl.textContent = text;
  }
}

async function updateStayAiServingRows(root) {
  const oneTimePriceEl = root.querySelector('[data-stayai-onetime-price]');
  const subscriptionPriceEl = root.querySelector('[data-stayai-subscription-price]');
  if (!oneTimePriceEl && !subscriptionPriceEl) return;

  const selectedPerServing = getSelectedVariantPerServing(root);
  if (selectedPerServing) {
    const oneTimeText = `${formatAmountWithSymbol(selectedPerServing.amount, selectedPerServing.symbol)}/SERVING`;
    upsertServingTextAfterPrice(oneTimePriceEl, oneTimeText, 'stayai-onetime-serving');

    const subscriptionPerServing = selectedPerServing.amount * 0.85;
    const subscriptionText = `${formatAmountWithSymbol(subscriptionPerServing, selectedPerServing.symbol)}/SERVING`;
    upsertServingTextAfterPrice(subscriptionPriceEl, subscriptionText, 'stayai-subscription-serving');
    return;
  }

  const fallbackServings = await getFallbackServingCount(root);
  if (!fallbackServings) return;

  // Do not overwrite variant-derived values if options were selected while awaiting fallback data.
  if (getSelectedVariantPerServing(root)) return;

  const oneTimePrice = parsePriceParts(extractPrice(oneTimePriceEl?.textContent || ''));
  if (oneTimePrice) {
    const oneTimePerServing = oneTimePrice.amount / fallbackServings;
    const oneTimeText = `${formatAmountWithSymbol(oneTimePerServing, oneTimePrice.symbol)}/SERVING`;
    upsertServingTextAfterPrice(oneTimePriceEl, oneTimeText, 'stayai-onetime-serving');
  }

  const subscriptionPrice = parsePriceParts(extractPrice(subscriptionPriceEl?.textContent || ''));
  if (subscriptionPrice) {
    const subscriptionPerServing = subscriptionPrice.amount / fallbackServings;
    const subscriptionText = `${formatAmountWithSymbol(subscriptionPerServing, subscriptionPrice.symbol)}/SERVING`;
    upsertServingTextAfterPrice(subscriptionPriceEl, subscriptionText, 'stayai-subscription-serving');
  }
}

async function updateStayAiOneTimeCompareAt(root) {
  const oneTimePriceEls = Array.from(root.querySelectorAll('[data-stayai-onetime-price]'));
  if (oneTimePriceEls.length === 0) return;

  const selectedVariantId = getSelectedVariantId(root);

  const productData = await getProductData(root);
  const variants = productData?.variants || [];
  if (variants.length === 0) return;

  let variant = null;
  if (selectedVariantId) {
    variant = variants.find((v) => Number(v?.id) === selectedVariantId) || null;
  }
  if (!variant && variants.length === 1) {
    variant = variants[0];
  }
  if (!variant) return;

  const selectedVariantIdText = String(variant.id);

  oneTimePriceEls.forEach((oneTimePriceEl) => {
    oneTimePriceEl.dataset.compareAtVariantId = selectedVariantIdText;
  });

  const compareAtAmount = Number(variant.compare_at_price) / 100;
  const currentAmount = Number(variant.price) / 100;
  const shouldShowCompareAt =
    Number.isFinite(compareAtAmount) && Number.isFinite(currentAmount) && compareAtAmount > currentAmount;

  oneTimePriceEls.forEach((oneTimePriceEl) => {
    const existingCompareAtEls = Array.from(oneTimePriceEl.querySelectorAll('s'));
    let compareAtEl = oneTimePriceEl.querySelector('.stayai-onetime-compare-at') || existingCompareAtEls[0] || null;

    if (!shouldShowCompareAt) {
      existingCompareAtEls.forEach((el) => el.remove());
      return;
    }

    // Ignore stale async responses if variant changed while awaiting fetch.
    if (oneTimePriceEl.dataset.compareAtVariantId !== selectedVariantIdText) return;

    const symbol = parsePriceParts(oneTimePriceEl.textContent || '')?.symbol || '$';
    const compareAtText = formatAmountWithSymbol(compareAtAmount, symbol);

    if (!compareAtEl) {
      compareAtEl = document.createElement('s');
      compareAtEl.className = 'stayai-onetime-compare-at';
      oneTimePriceEl.insertBefore(compareAtEl, oneTimePriceEl.firstChild);
    } else if (!compareAtEl.classList.contains('stayai-onetime-compare-at')) {
      compareAtEl.classList.add('stayai-onetime-compare-at');
    }

    // Keep only one compare-at node to prevent duplicate strikethrough prices.
    oneTimePriceEl.querySelectorAll('s').forEach((el) => {
      if (el !== compareAtEl) el.remove();
    });

    if (oneTimePriceEl.firstChild !== compareAtEl) {
      oneTimePriceEl.insertBefore(compareAtEl, oneTimePriceEl.firstChild);
    }

    if (compareAtEl.textContent !== compareAtText) {
      compareAtEl.textContent = compareAtText;
    }
  });
}

function updateButtons(root) {
  const selectedPrice = getSelectedPurchasePrice(root);
  const quantity = getQuantity(root);
  const buttons = Array.from(
    document.querySelectorAll(
      '.product-information .add-to-cart-text__content, .product-floating-bar .add-to-cart-text__content'
    )
  ).filter((el) => !el.closest('.recommended-product') && el.dataset.showPrice !== 'false');

  for (const el of buttons) {
    const current = el.textContent?.trim() || '';

    if (el.dataset.lastRenderedText && current && current !== el.dataset.lastRenderedText) {
      el.dataset.defaultText = current;
    }

    if (!el.dataset.defaultText) {
      el.dataset.defaultText = current;
    }

    const baseText = el.dataset.defaultText || current;
    const baseUnitPrice = selectedPrice?.price || extractPrice(baseText);
    const baseCompareAtPrice = selectedPrice?.compareAtPrice || '';
    const multipliedPrice = multiplyPriceByQuantity(baseUnitPrice, quantity);
    const multipliedCompareAtPrice = baseCompareAtPrice
      ? multiplyPriceByQuantity(baseCompareAtPrice, quantity)
      : '';

    if (!multipliedPrice) {
      el.textContent = el.dataset.defaultText || current;
      continue;
    }

    const label = stripButtonTrailingPrice(baseText) || baseText;
    renderButtonPrice(el, label, multipliedCompareAtPrice, multipliedPrice);
  }

  void updateStayAiServingRows(root);
  void updateStayAiOneTimeCompareAt(root);
}

function attachStayAiListeners(root) {
  const handler = () => updateButtons(root);
  const deferredHandler = () => requestAnimationFrame(handler);
  let scheduled = false;
  const scheduleHandler = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      handler();
    });
  };

  const quantityInput = root.querySelector("input[name='quantity']");
  quantityInput?.addEventListener('change', handler);
  quantityInput?.addEventListener('input', handler);
  quantityInput?.addEventListener('blur', handler);

  const variantInputs = root.querySelectorAll(
    "input[name='id'], select[name='id'], [name^='options'], [data-option-value-id]"
  );
  variantInputs.forEach((el) => {
    el.addEventListener('change', handler);
    el.addEventListener('input', handler);
  });

  root.addEventListener('quantity-selector:update', handler);
  document.addEventListener('quantity-selector:update', handler);
  document.addEventListener('variant:update', handler);
  document.addEventListener('variant:selected', handler);
  document.addEventListener('input', (event) => {
    if (event.target instanceof Element && event.target.matches("input[name='quantity']")) {
      handler();
    }
  });
  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;
    if (
      event.target.matches(
        "input[name='quantity'], [data-stayai-plan-select], [data-stayai-purchase-option] input, input[name^='stayai_purchase_type']"
      )
    ) {
      handler();
    }
  });
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.quantity-plus, .quantity-minus')) {
      deferredHandler();
    }
  });

  root.addEventListener('change', handler, { capture: true });
  root.addEventListener('input', handler, { capture: true });

  const observer = new MutationObserver(scheduleHandler);
  observer.observe(root, { subtree: true, childList: true });

  handler();
}

function initStayAiAtcSync() {
  const candidates = Array.from(
    document.querySelectorAll(
      '.stayai-widget, .stayai-subscription, [data-stayai-subscription-price], [data-stayai-onetime-price]'
    )
  );

  if (candidates.length === 0) return false;

  const legacyNode = candidates.find((node) => !node.closest('[data-stayai-clone]'));
  const cloneNode = candidates.find((node) => node.closest('[data-stayai-clone]'));
  const sourceNode = legacyNode || cloneNode;
  const stayAiRoot =
    sourceNode?.closest('.stayai-widget') ||
    sourceNode?.closest('[data-stayai-clone]') ||
    sourceNode?.closest('.stayai-subscription') ||
    sourceNode ||
    null;

  if (!stayAiRoot) return false;

  attachStayAiListeners(stayAiRoot.closest('form') || stayAiRoot);
  return true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (initStayAiAtcSync()) return;

    const observer = new MutationObserver(() => {
      if (initStayAiAtcSync()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
} else {
  if (!initStayAiAtcSync()) {
    const observer = new MutationObserver(() => {
      if (initStayAiAtcSync()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
}
